// V14.9 — Guest Public Display
// All hardcoded zinc / gray-* color classes replaced with Shadcn theme tokens
// (bg-card / bg-muted / text-foreground / text-muted-foreground / border-border)
// so light/dark modes follow the global theme automatically.
// Real-time: subscribes to new_post, post_updated, post_deleted, new_comment,
// new_reaction socket events with a 15s polling fallback.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';import { useParams, Link } from 'react-router-dom';
import { guestApi, interactionsApi } from '../../lib/mpApi';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import NewsArticleDetailDialog from '../../components/mp/NewsArticleDetailDialog';
// V14.13 — Use the SAME post templates the admin Live Preview uses, so
// the guest view renders pixel-identical Instagram / Facebook / news cards.
import { SocialPostTemplate, NewsTemplate } from '../../components/mp/SocialPostTemplates';
import GuestPasswordGate from '../../components/mp/GuestPasswordGate';
import { toast } from 'sonner';
import {
  Sun, Moon, Plus, User, Send, MessageCircle, RefreshCw, AlertCircle,
  Film, FileUp, X, Heart, CheckCircle, ChevronDown, ChevronUp, Share2, Loader2, Radio,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const formatDateTime = (dateStr) => dateStr ? new Date(dateStr).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '';
const formatNumber = (num) => { if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'; if (num >= 1000) return (num / 1000).toFixed(1) + 'K'; return num?.toString() || '0'; };
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;
const isVideo = (url) => url && ['mp4','webm','ogg','mov','avi','mkv'].includes(url.split('?')[0].split('.').pop().toLowerCase());

const CommentSection = ({ postId, guestId, guestToken }) => {
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  const loadComments = useCallback(async () => {
    try { setComments(await interactionsApi.getComments(postId)); } catch { /* noop */ }
  }, [postId]);

  useEffect(() => { if (showComments) loadComments(); }, [showComments, loadComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setLoading(true);
    try { await interactionsApi.createComment({ post_id: postId, content: newComment }, guestId, guestToken); setNewComment(''); loadComments(); }
    catch { toast.error('Register first to comment'); }
    finally { setLoading(false); }
  };

  return (
    <div className="border-t border-border pt-2 mt-2">
      <button onClick={() => setShowComments(!showComments)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
        {showComments ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {comments.length > 0 ? `${comments.length} comments` : 'Add comment'}
      </button>
      {showComments && (
        <div className="mt-2 space-y-2">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Write a comment..." className="flex-1 h-8 text-sm rounded-sm bg-muted border-input" disabled={loading} />
            <Button type="submit" size="sm" disabled={loading || !newComment.trim()} className="rounded-sm"><Send className="w-3 h-3" /></Button>
          </form>
          <div className="max-h-48 overflow-y-auto space-y-2">
            {comments.map(c => (
              <div key={c.comment_id} className="text-xs p-2 rounded-sm bg-muted text-foreground">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{c.author_name}</span>
                  {c.author_type === 'guest' && <Badge variant="outline" className="text-[10px] px-1 py-0 rounded-sm">Guest</Badge>}
                  <span className="text-muted-foreground">{formatDateTime(c.created_at)}</span>
                </div>
                <p className="mt-1">{c.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SocialPostCard = ({ post, guestId, guestToken, onReaction, onOpen }) => (
  // V14.13 — Re-use the admin SocialPostTemplate (same Instagram / Facebook /
  // Twitter / etc. renderers). Interaction layer + comments sit BELOW it.
  <div className="bg-card border border-border rounded-sm overflow-hidden">
    <SocialPostTemplate post={post} platform={post.platform}
      onInteract={(_, postId, type) => onReaction(postId, type === 'like' ? 'like' : type)}
      onOpen={onOpen} />
    <div className="px-3 pb-3 pt-2 border-t border-border">
      <CommentSection postId={post.post_id} guestId={guestId} guestToken={guestToken} />
    </div>
  </div>
);

const NewsCard = ({ post, guestId, guestToken, platformLookup, onOpen }) => {
  // V14.13 — Re-use the admin NewsTemplate so guest sees identical BBC/CNN/etc layouts.
  // V14.13.11.4 — Pass onOpen through so NewsTemplate renders its
  // "Read more →" affordance and handles click/keyboard itself.
  const plat = platformLookup[post.platform?.toLowerCase()] || {};
  const color = plat.color || '#333';
  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden">
      <NewsTemplate post={post} platformColor={color} onOpen={onOpen} />
      <div className="px-3 pb-3 pt-2 border-t border-border">
        <CommentSection postId={post.post_id} guestId={guestId} guestToken={guestToken} />
      </div>
    </div>
  );
};

const MPGuestPublicDisplayPage = () => {
  const { guestToken } = useParams();
  const [exercise, setExercise] = useState(null);
  const [posts, setPosts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState('dark');
  // V14.13.6 — Scope guest credentials by guestToken. Previously a single
  // global key (`mp_guest_id`) caused a 401 when a user with a stale guest
  // session opened a NEW guest link: we'd send the OLD guest_id paired with
  // the NEW guest_token, and the backend's
  // {"guest_id":..., "guest_token":...} lookup would fail.
  const guestIdKey = `mp_guest_id:${guestToken || ''}`;
  const guestNameKey = `mp_guest_name:${guestToken || ''}`;
  const [guestId, setGuestId] = useState(() => {
    // Migrate legacy un-scoped keys on first load.
    const scoped = localStorage.getItem(guestIdKey);
    if (scoped) return scoped;
    const legacy = localStorage.getItem('mp_guest_id');
    if (legacy) {
      // Don't trust the legacy id across exercises — clear it so the user
      // re-registers on this guest link.
      localStorage.removeItem('mp_guest_id');
      localStorage.removeItem('mp_guest_name');
    }
    return null;
  });
  const [guestName, setGuestName] = useState(localStorage.getItem(guestNameKey) || '');
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [showCreatePostDialog, setShowCreatePostDialog] = useState(false);
  const [newPost, setNewPost] = useState({ platform: '', content: '', title: '', post_type: 'social_media' });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [detailPost, setDetailPost] = useState(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);

  const platformLookup = {};
  platforms.forEach(p => { platformLookup[p.name.toLowerCase()] = p; });

  // Sync dark class on mount and theme change
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const [needsPassword, setNeedsPassword] = useState(false);

  const loadData = useCallback(async () => {
    if (!guestToken) return;
    try {
      setLoading(true); setError(null);
      const exerciseData = await guestApi.getExercise(guestToken);
      setExercise(exerciseData);
      setNeedsPassword(false);
      const postsData = await guestApi.getPosts(guestToken);
      setPosts(postsData);
      try {
        const res = await fetch(`${BACKEND_URL}/api/mp/platforms/public`);
        if (res.ok) setPlatforms(await res.json());
      } catch { /* noop */ }
    } catch (err) {
      // V14.13 — 401 + `guest_password_required` → show the password gate.
      if (err.response?.status === 401 && err.response?.data?.detail === 'guest_password_required') {
        setNeedsPassword(true);
        setError(null);
      } else {
        setError(err.response?.data?.detail || 'Failed to load exercise');
      }
    }
    finally { setLoading(false); }
  }, [guestToken]);

  // Realtime: socket subscriptions
  useEffect(() => {
    if (!guestToken) return;
    let socket;
    import('socket.io-client').then(({ io }) => {
      socket = io(BACKEND_URL, {
        path: '/api/socket.io',
        auth: { guest_token: guestToken },
        reconnection: true,
        reconnectionDelay: 2000,
        transports: ['websocket', 'polling'],
      });
      socket.on('new_post', (data) => {
        if (data?.data && data.data.status === 'published') {
          setPosts(prev => [data.data, ...prev.filter(p => p.post_id !== data.data.post_id)]);
          toast.info('New post');
        }
      });
      socket.on('post_updated', (data) => {
        if (data?.data) {
          setPosts(prev => prev.map(p => p.post_id === data.data.post_id ? data.data : p));
        }
      });
      socket.on('post_deleted', (data) => {
        if (data?.post_id) {
          setPosts(prev => prev.filter(p => p.post_id !== data.post_id));
        }
      });
      socket.on('post_published', (data) => {
        // Scheduled posts that just went live
        if (data?.data) {
          setPosts(prev => {
            const exists = prev.some(p => p.post_id === data.data.post_id);
            return exists ? prev.map(p => p.post_id === data.data.post_id ? data.data : p) : [data.data, ...prev];
          });
        }
      });
      socket.on('new_comment', () => loadData());
      socket.on('new_reaction', () => loadData());
      wsRef.current = socket;
    });
    return () => {
      if (wsRef.current) { wsRef.current.disconnect(); wsRef.current = null; }
    };
  }, [guestToken, loadData]);

  useEffect(() => { loadData(); }, [loadData]);

  // V14.13 — Polling fallback for like/comment sync (every 15s).
  // CRITICAL: We pause the poll while the Create Post dialog is open because
  // refreshing `exercise`/`posts` causes the platform <Select>'s derived
  // option list to re-render, which can drop the user's selection mid-typing.
  // V14.13.6 — Also pause on error/password gate to avoid the ~100-retry
  // storm observed in production logs (Auth Required loop every 15s).
  useEffect(() => {
    if (!guestToken) return;
    if (showCreatePostDialog) return;
    if (error || needsPassword) return;
    const interval = setInterval(() => {
      loadData();
    }, 15000);
    return () => clearInterval(interval);
  }, [guestToken, loadData, showCreatePostDialog, error, needsPassword]);

  const handleRegister = async () => {
    if (!registerName.trim()) { toast.error('Enter your name'); return; }
    try {
      const result = await guestApi.register(registerName, guestToken);
      setGuestId(result.guest_id); setGuestName(registerName);
      // V14.13.6 — Scope by guestToken (see comment on initial state).
      localStorage.setItem(guestIdKey, result.guest_id);
      localStorage.setItem(guestNameKey, registerName);
      setShowRegisterDialog(false);
      toast.success(`Welcome, ${registerName}!`);
    } catch {
      toast.error('Failed to register');
    }
  };

  const handleFileSelect = (e) => { setSelectedFiles(prev => [...prev, ...Array.from(e.target.files)]); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const handleCreatePost = async () => {
    if (!newPost.platform || !newPost.content) { toast.error('Select a platform and enter content'); return; }
    // V14.13.6 — Require BOTH guest_id AND guest_token before submitting.
    // The backend create_guest_post falls through to a 401 if either is missing.
    if (!guestId || !guestToken) {
      setShowRegisterDialog(true);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('exercise_id', exercise.exercise_id);
      formData.append('platform', newPost.platform);
      formData.append('content', newPost.content);
      formData.append('post_type', newPost.post_type);
      if (newPost.title) formData.append('title', newPost.title);
      // CRITICAL: guest credentials MUST be appended BEFORE files for older
      // proxies that buffer multipart in field-order. They're required by
      // /api/mp/guest/posts to skip the get_current_user fallback.
      formData.append('guest_id', guestId);
      formData.append('guest_token', guestToken);
      selectedFiles.forEach(file => formData.append('files', file));
      const response = await fetch(`${BACKEND_URL}/api/mp/guest/posts`, { method: 'POST', body: formData });
      if (response.status === 401) {
        // Stale guest session — clear and re-register.
        localStorage.removeItem(guestIdKey);
        localStorage.removeItem(guestNameKey);
        setGuestId(null);
        toast.error('Your guest session has expired. Please register again.');
        setShowRegisterDialog(true);
        return;
      }
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `Failed (${response.status})`);
      }
      toast.success('Post created!');
      setShowCreatePostDialog(false); setNewPost({ platform: '', content: '', title: '', post_type: 'social_media' }); setSelectedFiles([]); loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to create post');
    }
    finally { setUploading(false); }
  };

  const handleReaction = async (postId, reactionType = 'like') => {
    if (!guestId) { setShowRegisterDialog(true); return; }
    try { await interactionsApi.createReaction({ post_id: postId, reaction_type: reactionType }, guestId, guestToken); } catch { /* noop */ }
  };

  const toggleTheme = () => { setTheme(prev => prev === 'dark' ? 'light' : 'dark'); };
  const getPostsByPlatform = (name) => posts.filter(p => p.platform?.toLowerCase() === name?.toLowerCase() && p.status === 'published');
  // V14.13.11.4 — Memoise to keep the reference stable for the useEffect
  // that seeds `activePlatform`; otherwise a fresh array every render caused
  // an infinite deps-change loop and a fatal build warning under CI=true.
  const displayPlatforms = useMemo(
    () => (exercise?.selected_platforms?.length > 0
      ? exercise.selected_platforms
      : [...new Set(posts.map(p => p.platform))]),
    [exercise?.selected_platforms, posts]
  );
  const getWidthForPlatform = (name) => { const c = exercise?.platform_widths?.find(pw => pw.platform === name); return c ? c.width : Math.floor(100 / Math.max(displayPlatforms.length, 1)); };
  const isNewsPlatform2 = (name) => { const p = platformLookup[name?.toLowerCase()]; if (p?.category === 'news') return true; return ['bbc','cnn','reuters','aljazeera','nytimes','guardian','foxnews','rt','xinhua'].includes(name?.toLowerCase()); };
  const getPlatformDisplayName = (name) => platformLookup[name?.toLowerCase()]?.display_name || name;
  const getPlatformColor = (name) => platformLookup[name?.toLowerCase()]?.color || '#666';
  const getPlatformIcon = (name) => { const p = platformLookup[name?.toLowerCase()]; if (p?.custom_icon_url) return <img src={resolveUrl(p.custom_icon_url)} alt="" className="w-full h-full object-contain" />; return <span className="text-xs font-bold">{(p?.display_name || name)?.charAt(0).toUpperCase()}</span>; };

  // V14.13.11.2 — Mobile responsiveness for the guest feed.
  // On screens < 768px we collapse to a single-platform-at-a-time view with
  // a horizontal scrolling tab strip; on tablet/desktop we keep the existing
  // multi-column horizontal layout the simcels are used to.
  const [activePlatform, setActivePlatform] = useState(null);
  useEffect(() => {
    if (!activePlatform && displayPlatforms.length > 0) {
      setActivePlatform(displayPlatforms[0]);
    }
  }, [displayPlatforms, activePlatform]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" /><p className="mt-4 text-muted-foreground">Loading...</p></div>;
  if (needsPassword) return <GuestPasswordGate guestToken={guestToken} exerciseName={exercise?.name} onUnlocked={loadData} />;
  if (error) return <div className="min-h-screen flex flex-col items-center justify-center bg-background"><AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" /><h2 className="text-xl font-bold mb-2">Access Denied</h2><p className="text-muted-foreground">{error}</p></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="mp-guest-display">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        {/* Top row: title + LIVE + identity/actions. Buttons compress to
            icon-only at < sm and the desktop labels reappear on >= sm. */}
        <div className="flex h-14 items-center gap-2 px-3 sm:gap-4 sm:px-4">
          {exercise?.logo_url ? (
            <img src={exercise.logo_url.startsWith('/') ? `${BACKEND_URL}${exercise.logo_url}` : exercise.logo_url}
              alt="" className="w-8 h-8 sm:w-9 sm:h-9 rounded object-contain bg-white border border-border shrink-0" />
          ) : (
            <Radio className="w-5 h-5 text-primary shrink-0" />
          )}
          <h1 className="font-heading font-bold text-sm sm:text-lg tracking-tight uppercase truncate min-w-0">
            {exercise?.name || 'Media Portal'}
          </h1>
          <Badge className="bg-red-500/10 text-red-500 border-red-500/30 rounded-sm text-[10px] font-bold uppercase animate-live shrink-0">Live</Badge>
          <div className="flex-1" />
          {guestName ? (
            <Badge variant="secondary" className="gap-1 rounded-sm max-w-[120px] truncate" title={guestName}>
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate">{guestName}</span>
            </Badge>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setShowRegisterDialog(true)} className="rounded-sm" data-testid="guest-join-btn">
              <User className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Join</span>
            </Button>
          )}
          <Button size="sm" onClick={() => setShowCreatePostDialog(true)} data-testid="create-post-btn" className="rounded-sm">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Create Post</span>
          </Button>
          <Button variant="ghost" size="icon" onClick={loadData} className="rounded-sm hidden sm:inline-flex"><RefreshCw className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-sm">{theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
        </div>

        {/* Mobile-only platform tab strip. Horizontal scroll, snap, sticky
            under the title bar. Hidden on tablet+ where the full multi-column
            layout takes over. */}
        <div className="md:hidden border-t border-border overflow-x-auto bg-background/95">
          <div className="flex gap-1 px-2 py-1.5 min-w-max">
            {displayPlatforms.map((p) => {
              const isActive = p === activePlatform;
              const c = getPlatformColor(p);
              const count = getPostsByPlatform(p).length;
              return (
                <button
                  key={p}
                  onClick={() => setActivePlatform(p)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-colors ${
                    isActive ? 'text-white' : 'text-muted-foreground hover:bg-muted'
                  }`}
                  style={isActive ? { backgroundColor: c } : {}}
                  data-testid={`mobile-platform-tab-${p}`}
                >
                  <span className="w-3.5 h-3.5 inline-flex items-center justify-center">{getPlatformIcon(p)}</span>
                  {getPlatformDisplayName(p)}
                  <span className={`text-[10px] px-1 rounded ${isActive ? 'bg-white/20' : 'bg-muted'}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Tablet/desktop: keep the original multi-column horizontal layout. */}
      <div className="hidden md:flex h-[calc(100vh-3.5rem)] overflow-x-auto overflow-y-hidden mp-platform-strip">
        {displayPlatforms.map(platformName => {
          const width = getWidthForPlatform(platformName);
          const platformPosts = getPostsByPlatform(platformName);
          const isNews = isNewsPlatform2(platformName);
          const color = getPlatformColor(platformName);
          const dName = getPlatformDisplayName(platformName);
          return (
            <div key={platformName} className="border-r border-border flex-shrink-0 overflow-hidden bg-background" style={{ width: `${width}%`, minWidth: '280px' }}>
              <ScrollArea className="h-full">
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-border sticky top-0 bg-background/95 backdrop-blur z-10">
                    <div className="w-6 h-6 rounded-sm flex items-center justify-center text-white" style={{ backgroundColor: color }}>{getPlatformIcon(platformName)}</div>
                    <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-foreground">{dName}</h2>
                    <Badge variant="secondary" className="ml-auto text-xs rounded-sm">{platformPosts.length}</Badge>
                  </div>
                  {platformPosts.length === 0 ? <div className="text-center py-8 text-muted-foreground"><p className="text-sm">No posts yet</p></div> : (
                    <div className="space-y-4">
                      {platformPosts.map(post => isNews
                        ? <NewsCard key={post.post_id} post={post} guestId={guestId} guestToken={guestToken} platformLookup={platformLookup} onOpen={() => setDetailPost(post)} />
                        : <SocialPostCard key={post.post_id} post={post} guestId={guestId} guestToken={guestToken} onReaction={handleReaction} onOpen={() => setDetailPost(post)} />
                      )}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          );
        })}
        {displayPlatforms.length === 0 && <div className="flex-1 flex items-center justify-center"><p className="text-muted-foreground">No platforms configured</p></div>}
      </div>

      {/* Mobile: single-platform view that follows the active tab. */}
      <div className="md:hidden">
        {!activePlatform ? (
          <div className="py-20 text-center text-muted-foreground text-sm">No platforms configured</div>
        ) : (() => {
          const platformPosts = getPostsByPlatform(activePlatform);
          const isNews = isNewsPlatform2(activePlatform);
          return (
            <div className="px-3 py-3">
              {platformPosts.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                  <p className="text-sm">No posts yet on {getPlatformDisplayName(activePlatform)}.</p>
                  <p className="text-xs mt-1">Tap “Create Post” to add the first one.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {platformPosts.map((post) => isNews
                    ? <NewsCard key={post.post_id} post={post} guestId={guestId} guestToken={guestToken} platformLookup={platformLookup} onOpen={() => setDetailPost(post)} />
                    : <SocialPostCard key={post.post_id} post={post} guestId={guestId} guestToken={guestToken} onReaction={handleReaction} onOpen={() => setDetailPost(post)} />
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      <NewsArticleDetailDialog open={!!detailPost} onOpenChange={(o) => !o && setDetailPost(null)} post={detailPost} />

      {/* Register */}
      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent className="rounded-sm w-[calc(100vw-1.5rem)] sm:w-full">
          <DialogHeader><DialogTitle className="font-heading uppercase">Join as Guest</DialogTitle><DialogDescription>Enter your name to interact</DialogDescription></DialogHeader>
          <div className="py-4">
            <Label className="text-xs uppercase tracking-wider">Your Name</Label>
            <Input value={registerName} onChange={(e) => setRegisterName(e.target.value)} placeholder="Enter your name" className="mt-2 rounded-sm" data-testid="guest-name-input" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowRegisterDialog(false)} className="rounded-sm">Cancel</Button><Button onClick={handleRegister} disabled={!registerName.trim()} data-testid="guest-register-btn" className="rounded-sm">Join</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Post */}
      <Dialog open={showCreatePostDialog} onOpenChange={setShowCreatePostDialog}>
        <DialogContent className="max-w-lg rounded-sm w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto sm:w-full">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-wide">Create New Post</DialogTitle><DialogDescription>Share content with exercise participants</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider">Platform</Label>
              <Select value={newPost.platform} onValueChange={(v) => setNewPost({ ...newPost, platform: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select platform" /></SelectTrigger>
                <SelectContent>{displayPlatforms.map(p => <SelectItem key={p} value={p}>{getPlatformDisplayName(p)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider">Post Type</Label>
              <Select value={newPost.post_type} onValueChange={(v) => setNewPost({ ...newPost, post_type: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="social_media">Social Media</SelectItem><SelectItem value="news">News Article</SelectItem></SelectContent>
              </Select>
            </div>
            {newPost.post_type === 'news' && <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider">Title</Label><Input value={newPost.title} onChange={(e) => setNewPost({ ...newPost, title: e.target.value })} placeholder="Headline" className="rounded-sm" /></div>}
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider">Content</Label><Textarea value={newPost.content} onChange={(e) => setNewPost({ ...newPost, content: e.target.value })} placeholder="What's on your mind?" rows={4} className="rounded-sm" data-testid="new-post-content" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider">Media</Label>
              <div className="border-2 border-dashed border-border rounded-sm p-4 text-center">
                <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx" onChange={handleFileSelect} className="hidden" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="rounded-sm" data-testid="upload-media-btn"><FileUp className="w-4 h-4 mr-2" />Add Files</Button>
              </div>
              {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedFiles.map((file, i) => (
                    <div key={i} className="relative group">
                      <div className="w-16 h-16 border border-border rounded-sm overflow-hidden bg-muted flex items-center justify-center">
                        {file.type.startsWith('video/') ? <Film className="w-6 h-6 text-muted-foreground" /> : file.type.startsWith('image/') ? <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" /> : <div className="text-[10px] text-muted-foreground">{file.name.split('.').pop()?.toUpperCase()}</div>}
                      </div>
                      <button type="button" onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePostDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreatePost} disabled={uploading} data-testid="submit-post-btn" className="rounded-sm">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Send className="w-4 h-4 mr-2" />Post</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MPGuestPublicDisplayPage;
